import { useCallback, useMemo, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { format } from "date-fns";
import i18next from "i18next";
import { useFormContext } from "react-hook-form";
import ButtonMenu from "../../../components/Buttons/ButtonMenu";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getNameBusinessTheme } from "../../../actions/getters";
import { startReportPdfExport } from "../../../actions/reportPdf";
import { startGetAdsUser } from "../../../actions/user";
import { buildCampaignReportPdfSnapshot } from "../../../features/reportPdf/buildCampaignReportPdfSnapshot";
import {
  BUTTON_DOWNLOAD_REPORT,
  GLOBAL_REPORT_CUSTOM_OPTION,
  GLOBAL_REPORT_DOWNLOAD_NOT_READY,
  GLOBAL_REPORT_PDF_OPTION,
  REPORT_GENERATED_BY_BUZZLY_AI,
} from "../../../i18n/keysTranslations";
import { PRIMARY_COLOR_TEXT } from "../../../utils/colors";

import { LaunchError } from "../../../utils/errors";

const REPORT_TYPE_CAMPAIGN = "campaign";
const DATE_FORMAT = "yyyy-MM-dd";

const sanitizeFileNamePart = (value) => {
  const cleaned = String(value || "campaign")
    // eslint-disable-next-line
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return cleaned || "campaign";
};

const formatDay = (value) => {
  if (!value) {
    return "fecha";
  }
  return format(new Date(value), DATE_FORMAT);
};

const triggerBrowserDownload = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.target = "_blank";
  anchor.rel = "noopener";
  anchor.click();
};

const CampaignReportPdfExportButton = ({
  filteredLogs,
  isReady,
  learnings,
  summaryText,
  onOpenCustomReport,
  disabled = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const businessName = dispatch(getNameBusinessTheme());
  const [isExporting, setIsExporting] = useState(false);
  const {
    campaign,
    campaignID,
    currenciesConfiguration,
    getValues,
    isEmptyReport,
    reportConfiguration,
    reportDateLimits,
  } = useFormContext();

  const onExport = useCallback(async () => {
    if (!isReady || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const dateRange = getValues("dateRange");
      const breakdown = getValues("breakdown");
      const platform = campaign?.platforms?.[0];
      const campaignName =
        campaign?.platformsData?.[platform]?.name || campaign?.name;
      const fileName = `Reporte_${sanitizeFileNamePart(campaignName)}_${formatDay(
        reportConfiguration?.startDateTime
      )}_${formatDay(reportConfiguration?.endDateTime)}.pdf`;
      const userIDs = [
        ...new Set(
          (filteredLogs || []).map((log) => log?.userID).filter(Boolean)
        ),
      ];
      const userNames = {};
      await Promise.all(
        userIDs.map(async (userID) => {
          const adsUser = await dispatch(startGetAdsUser(userID));
          if (adsUser?.name) {
            userNames[userID] = adsUser.name;
          }
        })
      );
      const snapshot = buildCampaignReportPdfSnapshot({
        campaignID,
        campaign,
        currenciesConfiguration,
        reportConfiguration,
        reportDateLimits,
        dateRange,
        breakdown,
        filteredLogs,
        userNames,
        summaryText,
        learnings,
        isEmptyReport,
        language: i18next.language,
      });
      const footerText = t(REPORT_GENERATED_BY_BUZZLY_AI, {
        NAME_BUSINESS: businessName,
      });

      const response = await dispatch(
        startReportPdfExport({
          reportType: REPORT_TYPE_CAMPAIGN,
          snapshot,
          fileName,
          footerText,
        })
      );

      if (response?.ok && response.url) {
        triggerBrowserDownload(response.url, fileName);
      } else {
        console.error("Error exporting campaign report PDF:", response);
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "CampaignReportPdfExportButton",
          capture: "exception",
        })
      );
      console.error("Error exporting campaign report PDF:", error);
    } finally {
      setIsExporting(false);
    }
  }, [
    businessName,
    campaign,
    campaignID,
    currenciesConfiguration,
    dispatch,
    filteredLogs,
    getValues,
    isEmptyReport,
    isExporting,
    isReady,
    learnings,
    reportConfiguration,
    reportDateLimits,
    summaryText,
    t,
  ]);

  const downloadOptions = useMemo(
    () => [
      {
        label: GLOBAL_REPORT_PDF_OPTION,
        onClick: onExport,
        disabled: !isReady || isExporting,
        tooltip:
          !isReady && !isExporting
            ? GLOBAL_REPORT_DOWNLOAD_NOT_READY
            : undefined,
      },
      {
        label: GLOBAL_REPORT_CUSTOM_OPTION,
        onClick: onOpenCustomReport,
        disabled,
      },
    ],
    [disabled, isExporting, isReady, onExport, onOpenCustomReport]
  );

  return (
    <ButtonMenu
      mode="icon"
      tooltipText={t(BUTTON_DOWNLOAD_REPORT)}
      iconButton={
        <DownloadIcon
          sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
        />
      }
      options={downloadOptions}
      edge={false}
      loading={isExporting}
      disabled={disabled}
    />
  );
};

export default CampaignReportPdfExportButton;
